import jsPDF from "jspdf";
import "jspdf-autotable";
import { getImageDataUrlFromServer } from "../../utils/EmbedFields";

function parseHTMLToParagraphs(htmlString) {
  const container = document.createElement("div");
  container.innerHTML = htmlString;

  const paragraphs = [];

  // Helper function to count leading whitespace (spaces, tabs, and &nbsp;)
  const countLeadingWhitespace = (element) => {
    // Get the raw text including &nbsp; entities
    const firstTextNode = getFirstTextNode(element);
    if (!firstTextNode) {
      return { spaces: 0, tabs: 0, nbsp: 0, totalIndent: 0 };
    }

    const text = firstTextNode.textContent;
    let spaces = 0;
    let tabs = 0;
    let nbsp = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charCode = text.charCodeAt(i);

      if (char === "\t") {
        tabs++;
      } else if (char === " " && charCode === 32) {
        spaces++;
      } else if (charCode === 160) {
        nbsp++;
      } else if (char !== " " && charCode !== 160) {
        break;
      }
    }

    // Calculate total indent: each tab = 4 spaces, each nbsp = 1 space
    // Group every 4 spaces/nbsp as one indent level
    const totalSpaces = spaces + nbsp + tabs * 4;
    const totalIndent = Math.floor(totalSpaces / 4);

    return { spaces, tabs, nbsp, totalIndent, totalSpaces };
  };

  // Helper function to get first text node
  const getFirstTextNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }
    for (let child of node.childNodes) {
      const textNode = getFirstTextNode(child);
      if (textNode) return textNode;
    }
    return null;
  };

  // Helper function to extract text alignment
  const getAlignment = (element) => {
    const classList = element.className || "";
    if (classList.includes("ql-align-center")) return "center";
    if (classList.includes("ql-align-right")) return "right";
    if (classList.includes("ql-align-justify")) return "justify";

    if (element.style && element.style.textAlign) {
      return element.style.textAlign;
    }

    return "left";
  };

  // Helper function to process list items recursively
  const processListItem = (li, index, parentType, baseIndent = 0) => {
    const whitespace = countLeadingWhitespace(li);
    const itemWords = parseNodeToSegments(li, {}, true, true); // skipNested = true

    if (itemWords.length > 0) {
      const dataList = li.getAttribute("data-list");
      const listType =
        dataList === "bullet"
          ? "unordered-list"
          : dataList === "ordered"
          ? "ordered-list"
          : parentType === "ol"
          ? "ordered-list"
          : "unordered-list";

      const listItem = {
        type: listType,
        words: itemWords,
        indentLevel: baseIndent, // Use baseIndent directly for nesting
        totalSpaces: whitespace.totalSpaces,
        leadingSpaces: whitespace.spaces,
        leadingTabs: whitespace.tabs,
        leadingNbsp: whitespace.nbsp,
        alignment: getAlignment(li),
        nestingLevel: baseIndent, // Add explicit nesting level
      };

      if (listType === "ordered-list") {
        listItem.number = index + 1;
      }

      paragraphs.push(listItem);
    }

    // Process nested lists
    const nestedLists = Array.from(li.children).filter(
      (child) =>
        child.tagName.toLowerCase() === "ol" ||
        child.tagName.toLowerCase() === "ul"
    );

    nestedLists.forEach((nestedList) => {
      const nestedItems = Array.from(nestedList.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );
      nestedItems.forEach((nestedLi, nestedIndex) => {
        processListItem(
          nestedLi,
          nestedIndex,
          nestedList.tagName.toLowerCase(),
          baseIndent + 1 // Increment nesting level
        );
      });
    });
  };

  container.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      // Handle paragraphs
      if (tagName === "p") {
        const whitespace = countLeadingWhitespace(node);
        const paragraphWords = parseNodeToSegments(node, {}, true); // stripLeading = true
        if (paragraphWords.length > 0) {
          paragraphs.push({
            type: "paragraph",
            words: paragraphWords,
            indentLevel: whitespace.totalIndent,
            totalSpaces: whitespace.totalSpaces,
            leadingSpaces: whitespace.spaces,
            leadingTabs: whitespace.tabs,
            leadingNbsp: whitespace.nbsp,
            alignment: getAlignment(node),
          });
        }
      }
      // Handle headers (h1, h2, h3, h4, h5, h6)
      else if (
        tagName === "h1" ||
        tagName === "h2" ||
        tagName === "h3" ||
        tagName === "h4" ||
        tagName === "h5" ||
        tagName === "h6"
      ) {
        const whitespace = countLeadingWhitespace(node);
        const headerWords = parseNodeToSegments(node, {}, true);
        if (headerWords.length > 0) {
          paragraphs.push({
            type: "header",
            level: parseInt(tagName.substring(1)), // Extract number from h1, h2, etc.
            words: headerWords,
            indentLevel: whitespace.totalIndent,
            totalSpaces: whitespace.totalSpaces,
            leadingSpaces: whitespace.spaces,
            leadingTabs: whitespace.tabs,
            leadingNbsp: whitespace.nbsp,
            alignment: getAlignment(node),
          });
        }
      }
      // Handle lists (both ol and ul, checking data-list attribute on li elements)
      else if (tagName === "ol" || tagName === "ul") {
        const listItems = Array.from(node.children).filter(
          (child) => child.tagName.toLowerCase() === "li"
        );
        listItems.forEach((li, index) => {
          processListItem(li, index, tagName, 0);
        });
      }
    }
  });

  return paragraphs;
}

function parseNodeToSegments(
  node,
  style = {},
  stripLeading = false,
  skipNested = false
) {
  const words = [];
  let isFirstTextNode = true;

  function processNode(node, style = {}) {
    if (node.nodeType === Node.TEXT_NODE) {
      let textContent = node.textContent;

      // Strip leading whitespace from the first text node if requested
      if (stripLeading && isFirstTextNode) {
        textContent = textContent.replace(/^[\s\t]+/, "");
        isFirstTextNode = false;
      }

      const rawWords = textContent.split(/(\s+)/);
      rawWords.forEach((word) => {
        if (word !== "") {
          words.push({
            text: word,
            bold: style.bold || false,
            italic: style.italic || false,
            underline: style.underline || false,
          });
        }
      });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      const classList = node.className || "";

      // Skip Quill UI elements (like bullet markers)
      if (classList.includes("ql-ui")) {
        return;
      }

      if (skipNested && (tag === "ol" || tag === "ul")) {
        return;
      }

      const newStyle = { ...style };

      // Handle formatting tags
      if (tag === "b" || tag === "strong") newStyle.bold = true;
      if (tag === "i" || tag === "em") newStyle.italic = true;
      if (tag === "u") newStyle.underline = true;

      // Handle inline elements (span, a, code, etc.)
      if (tag === "span" || tag === "a" || tag === "code") {
        // Check for inline styles
        const inlineStyle = node.style;
        if (inlineStyle) {
          if (
            inlineStyle.fontWeight === "bold" ||
            parseInt(inlineStyle.fontWeight) >= 700
          ) {
            newStyle.bold = true;
          }
          if (inlineStyle.fontStyle === "italic") {
            newStyle.italic = true;
          }
          if (
            inlineStyle.textDecoration &&
            inlineStyle.textDecoration.includes("underline")
          ) {
            newStyle.underline = true;
          }
        }
      }

      // Handle line breaks
      if (tag === "br") {
        words.push({
          text: "\n",
          bold: false,
          italic: false,
          underline: false,
          isLineBreak: true,
        });
        return;
      }

      // Process children
      node.childNodes.forEach((child) => processNode(child, newStyle));
    }
  }

  processNode(node, style);
  return words;
}

function getWordWidth(
  doc,
  word,
  fontSize = 12,
  forceBold = false,
  forceItalic = false
) {
  let fontStyle = "normal";
  const isBold = forceBold || word.bold;
  const isItalic = forceItalic || word.italic;

  if (isBold && isItalic) fontStyle = "bolditalic";
  else if (isBold) fontStyle = "bold";
  else if (isItalic) fontStyle = "italic";

  doc.setFont("times", fontStyle);
  doc.setFontSize(fontSize);

  return doc.getTextWidth(word.text);
}

function drawJustifiedParagraphs(
  doc,
  htmlText,
  x,
  y,
  maxWidth,
  lineHeight,
  pageHeight,
  bottomMargin = 40,
  paragraphIndent = 40,
  footerHeight = 0,
  firstParagraphIndent = null,
  listIndent = 40,
  spaceIndentSize = 10, // Points per space (adjust this value)
  tabIndentSize = 40 // Points per tab (adjust this value)
) {
  const paragraphs = parseHTMLToParagraphs(htmlText);
  const effectiveBottomMargin = bottomMargin + footerHeight;

  paragraphs.forEach((paragraph, paragraphIndex) => {
    let lineWords = [];
    let lineWidth = 0;
    let isFirstLineOfParagraph = true;

    // Calculate indent from leading whitespace
    // Use totalSpaces for more granular control (every space counts)
    const whitespaceIndent = (paragraph.totalSpaces || 0) * spaceIndentSize;

    // Determine indent based on paragraph type
    let currentParagraphIndent;
    let bulletOffset = 0;
    let bulletText = "";
    let listLeftMargin = 0;

    // Determine font size and style for headers
    let fontSize = 12;
    let isBold = false;

    if (paragraph.type === "header") {
      // Set font size based on header level
      const headerSizes = {
        1: 24, // h1
        2: 20, // h2
        3: 16, // h3
        4: 14, // h4
        5: 12, // h5
        6: 10, // h6
      };
      fontSize = headerSizes[paragraph.level] || 12;
      isBold = true; // Headers are typically bold

      // Use whitespace indent or default paragraph indent
      if (whitespaceIndent > 0) {
        currentParagraphIndent = whitespaceIndent;
      } else {
        currentParagraphIndent =
          paragraphIndex === 0 && firstParagraphIndent !== null
            ? firstParagraphIndent
            : 0; // Headers typically don't have indent
      }
    } else if (paragraph.type === "ordered-list") {
      bulletText = `${paragraph.number}. `;
      bulletOffset = 25;
      currentParagraphIndent = 0;
      // Calculate indent based on nesting level
      const nestingLevel = paragraph.nestingLevel || paragraph.indentLevel || 0;
      listLeftMargin = listIndent + nestingLevel * 30; // 30 points per nesting level
    } else if (paragraph.type === "unordered-list") {
      bulletText = "• ";
      bulletOffset = 20;
      currentParagraphIndent = 0;
      // Calculate indent based on nesting level
      const nestingLevel = paragraph.nestingLevel || paragraph.indentLevel || 0;
      listLeftMargin = listIndent + nestingLevel * 30; // 30 points per nesting level
    } else {
      // Regular paragraph - use whitespace indent or default
      if (whitespaceIndent > 0) {
        currentParagraphIndent = whitespaceIndent;
      } else {
        currentParagraphIndent =
          paragraphIndex === 0 && firstParagraphIndent !== null
            ? firstParagraphIndent
            : paragraphIndent;
      }
    }

    const flushLine = (lineWords, isLastLine = false, isFirstLine = false) => {
      if (lineWords.length === 0) return y;

      const visibleWords = lineWords.filter(
        (w) => !/^\s+$/.test(w.text) && !w.isLineBreak
      );

      if (visibleWords.length === 0) {
        y += lineHeight;
        return y;
      }

      // Calculate start position and available width
      let startX = x;
      let effectiveMaxWidth = maxWidth; // This is the total available width from x to right margin

      // Handle text alignment
      const alignment = paragraph.alignment || "left";

      if (
        isFirstLine &&
        (paragraph.type === "ordered-list" ||
          paragraph.type === "unordered-list")
      ) {
        // Draw bullet/number
        doc.setFont("times", "normal");
        doc.setFontSize(fontSize);
        startX = x + listLeftMargin;
        doc.text(bulletText, startX, y);
        startX = x + listLeftMargin + bulletOffset;
        effectiveMaxWidth = maxWidth - (startX - x); // ← FIXED: Width from new startX to right margin
      } else if (
        isFirstLine &&
        (paragraph.type === "paragraph" || paragraph.type === "header")
      ) {
        startX = x + currentParagraphIndent;
        effectiveMaxWidth = maxWidth - (startX - x); // ← FIXED: Width from new startX to right margin
      } else if (
        paragraph.type === "ordered-list" ||
        paragraph.type === "unordered-list"
      ) {
        // Continuation lines for lists (hanging indent)
        startX = x + listLeftMargin + bulletOffset;
        effectiveMaxWidth = maxWidth - (startX - x); // ← FIXED: Width from new startX to right margin
      } else {
        startX = x;
        effectiveMaxWidth = maxWidth;
      }

      const totalWordsWidth = visibleWords.reduce(
        (acc, w) =>
          acc + getWordWidth(doc, w, fontSize, isBold || w.bold, w.italic),
        0
      );

      // Handle different text alignments
      const numGaps = visibleWords.length - 1;
      let extraSpace = 0;
      let alignmentOffset = 0;

      if (alignment === "center") {
        alignmentOffset = (effectiveMaxWidth - totalWordsWidth) / 2;
      } else if (alignment === "right") {
        alignmentOffset = effectiveMaxWidth - totalWordsWidth;
      } else if (alignment === "justify" || alignment === "left") {
        extraSpace =
          !isLastLine && numGaps > 0 && alignment === "justify"
            ? (effectiveMaxWidth - totalWordsWidth) / numGaps
            : 0;
      }

      let currentX = startX + alignmentOffset;
      let visibleWordIndex = 0;

      for (let i = 0; i < lineWords.length; i++) {
        const word = lineWords[i];
        const isSpace = /^\s+$/.test(word.text);
        const isLineBreak = word.isLineBreak;

        if (isLineBreak) {
          break;
        } else if (!isSpace) {
          let fontStyle = "normal";
          const wordBold = isBold || word.bold;
          if (wordBold && word.italic) fontStyle = "bolditalic";
          else if (wordBold) fontStyle = "bold";
          else if (word.italic) fontStyle = "italic";

          doc.setFont("times", fontStyle);
          doc.setFontSize(fontSize);

          const wordWidth = doc.getTextWidth(word.text);

          if (word.underline) {
            doc.line(currentX, y + 1, currentX + wordWidth, y + 1);
          }

          doc.text(word.text, currentX, y);
          currentX += wordWidth;

          if (
            alignment === "justify" &&
            !isLastLine &&
            visibleWordIndex < numGaps
          ) {
            currentX += extraSpace;
          } else if (
            alignment !== "justify" &&
            visibleWordIndex < visibleWords.length - 1
          ) {
            doc.setFont("times", "normal");
            doc.setFontSize(fontSize);
            currentX += doc.getTextWidth(" ");
          }

          visibleWordIndex++;
        } else {
          if (isLastLine || alignment !== "justify") {
            doc.setFont("times", "normal");
            doc.setFontSize(fontSize);
            currentX += doc.getTextWidth(" ");
          }
        }
      }

      y += lineHeight * (fontSize / 12); // Adjust line height based on font size

      if (y + lineHeight > pageHeight - effectiveBottomMargin) {
        doc.addPage();
        y = bottomMargin + 30;
      }

      return y;
    };

    const paragraphWords = paragraph.words;

    for (let i = 0; i < paragraphWords.length; i++) {
      const word = paragraphWords[i];

      if (word.isLineBreak) {
        if (lineWords.length > 0) {
          y = flushLine(lineWords, true, isFirstLineOfParagraph);
        } else {
          y += lineHeight;
        }
        lineWords = [];
        lineWidth = 0;
        isFirstLineOfParagraph = false;
        continue;
      }

      const wordWidth = getWordWidth(doc, word, fontSize, isBold, false);

      // Calculate ACTUAL available width based on where text starts
      let availableWidth;
      if (isFirstLineOfParagraph) {
        if (paragraph.type === "paragraph" || paragraph.type === "header") {
          // Text starts at x + currentParagraphIndent, can extend to x + maxWidth
          availableWidth = maxWidth - currentParagraphIndent;
        } else {
          // List: text starts at x + listLeftMargin + bulletOffset
          availableWidth = maxWidth - listLeftMargin - bulletOffset;
        }
      } else {
        if (
          paragraph.type === "ordered-list" ||
          paragraph.type === "unordered-list"
        ) {
          // Continuation line: same as first line for lists
          availableWidth = maxWidth - listLeftMargin - bulletOffset;
        } else {
          // Continuation line for paragraphs: starts at x, full width available
          availableWidth = maxWidth;
        }
      }

      // Add space width for non-first words
      const spaceWidth = lineWords.length > 0 ? doc.getTextWidth(" ") : 0;

      if (
        lineWidth + spaceWidth + wordWidth > availableWidth &&
        lineWords.length > 0
      ) {
        y = flushLine(lineWords, false, isFirstLineOfParagraph);
        lineWords = [];
        lineWidth = 0;
        isFirstLineOfParagraph = false;
      }

      lineWords.push(word);
      lineWidth += wordWidth + spaceWidth;
    }

    if (lineWords.length > 0) {
      y = flushLine(lineWords, true, isFirstLineOfParagraph);
    }

    // Add spacing after paragraphs and list items
    if (paragraphIndex < paragraphs.length - 1) {
      const nextParagraph = paragraphs[paragraphIndex + 1];
      const isCurrentList =
        paragraph.type === "ordered-list" ||
        paragraph.type === "unordered-list";
      const isNextList =
        nextParagraph.type === "ordered-list" ||
        nextParagraph.type === "unordered-list";
      const isCurrentHeader = paragraph.type === "header";
      const isNextHeader = nextParagraph.type === "header";

      if (isCurrentList && isNextList) {
        // Between list items: small spacing
        y += lineHeight * 0.2;
      } else if (isCurrentHeader || isNextHeader) {
        // Before or after headers: larger spacing
        y += lineHeight * 0.8;
      } else if (isCurrentList && !isNextList) {
        // After last list item, before paragraph: normal spacing
        y += lineHeight * 0.5;
      } else if (!isCurrentList && isNextList) {
        // Before first list item, after paragraph: normal spacing
        y += lineHeight * 0.5;
      } else {
        // Between paragraphs: normal spacing
        y += lineHeight * 0.5;
      }
    }
  });

  return y;
}

function splitTextToWidth(doc, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const testLine = currentLine ? currentLine + " " + word : word;
    const testWidth = doc.getTextWidth(testLine);

    if (testWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

const addFooterToPages = async (
  doc,
  footerImageDataUrl,
  pageWidth,
  pageHeight,
  mainDocumentPageCount
) => {
  let footerHeight = 0;

  const getFooterImageData = async (imageUrl) => {
    let dataUrl = imageUrl;
    if (imageUrl.includes("https://")) {
      dataUrl = await getImageDataUrlFromServer(imageUrl);
    }
    if (!dataUrl) return null;

    const imgProps = doc.getImageProperties(dataUrl);
    const aspectRatio = imgProps.width / imgProps.height;

    const maxHeight = pageHeight * 0.1;
    const maxWidth = pageWidth * 0.9;

    let renderHeight = maxHeight;
    let renderWidth = renderHeight * aspectRatio;

    if (renderWidth > maxWidth) {
      renderWidth = maxWidth;
      renderHeight = renderWidth / aspectRatio;
    }

    const xPos = (pageWidth - renderWidth) / 2;
    const yPos = pageHeight - renderHeight - 20;

    return {
      dataUrl,
      width: renderWidth,
      height: renderHeight,
      x: xPos,
      y: yPos,
    };
  };

  let footerImage = null;
  if (footerImageDataUrl) {
    footerImage = await getFooterImageData(footerImageDataUrl);
    if (footerImage) {
      footerHeight = footerImage.height;
    }
  }

  if (footerImage) {
    for (let i = 1; i <= mainDocumentPageCount; i++) {
      doc.setPage(i);
      doc.addImage(
        footerImage.dataUrl,
        "PNG",
        footerImage.x,
        footerImage.y,
        footerImage.width,
        footerImage.height
      );
    }
  }

  return footerHeight;
};

const generateLogsPDF = async (
  log,
  template,
  action = "preview",
  logType = "secretary"
) => {
  const doc = new jsPDF("p", "pt", [612, 936]);

  // console.log(log);

  doc.setProperties({
    title: template?.name,
    subject: log?.task || "",
    author: log?.secretary || log?.processedBy || "",
  });

  const contentMargin = 72;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const parseHTMLToText = (html) => {
    if (!html) return "-";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "-";
  };

  const parseHTMLToFormattedText = (html) => {
    if (!html) return [];

    const div = document.createElement("div");
    div.innerHTML = html;

    const items = [];

    const lists = div.querySelectorAll("ol, ul");
    lists.forEach((list) => {
      const listItems = list.querySelectorAll("li");
      listItems.forEach((li) => {
        const text = li.textContent.trim();
        if (text) {
          items.push({ type: "bullet", text });
        }
      });
    });

    if (items.length === 0) {
      const paragraphs = div.querySelectorAll("p");
      if (paragraphs.length > 0) {
        paragraphs.forEach((p) => {
          const text = p.textContent.trim();
          if (text) {
            items.push({ type: "text", text });
          }
        });
      } else {
        const text = div.textContent.trim();
        if (text) {
          const lines = text.split("\n").filter((line) => line.trim());
          lines.forEach((line) => {
            items.push({ type: "text", text: line.trim() });
          });
        }
      }
    }

    return items.length > 0 ? items : [{ type: "text", text: "-" }];
  };

  const getImageDataUrl = async (imageUrl) => {
    try {
      if (imageUrl.startsWith("data:")) {
        return imageUrl;
      }

      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error loading image:", error);
      return null;
    }
  };

  let currentY = margin;

  if (log?.recordType === "nte") {
    // Calculate footer height first
    let footerHeight = 0;
    if (template?.footer) {
      let footerDataUrl = template?.footer;
      if (footerDataUrl.includes("https://")) {
        footerDataUrl = await getImageDataUrlFromServer(footerDataUrl);
      }
      if (footerDataUrl) {
        const imgProps = doc.getImageProperties(footerDataUrl);
        const aspectRatio = imgProps.width / imgProps.height;
        footerHeight = (pageWidth * 0.9) / aspectRatio;
        const maxFooterHeight = pageHeight * 0.1;
        if (footerHeight > maxFooterHeight) {
          footerHeight = maxFooterHeight;
        }
      }
    }

    // Header image handling
    const MAX_HEADER_HEIGHT = 150;
    const MAX_HEADER_WIDTH = pageWidth;
    let headerImageHeight = 0;

    const drawHeaderImage = async (imageUrl) => {
      try {
        const imageDataUrl = await getImageDataUrl(imageUrl);
        if (!imageDataUrl) {
          console.error("Failed to load image");
          return 50;
        }

        const imgProps = doc.getImageProperties(imageDataUrl);
        const imgWidth = imgProps.width;
        const imgHeight = imgProps.height;
        const aspectRatio = imgWidth / imgHeight;

        let renderWidth = MAX_HEADER_WIDTH;
        let renderHeight = renderWidth / aspectRatio;

        if (renderHeight > MAX_HEADER_HEIGHT) {
          renderHeight = MAX_HEADER_HEIGHT;
          renderWidth = renderHeight * aspectRatio;
        }

        const xPos = (pageWidth - renderWidth) / 2;
        const yPos = 0;

        doc.addImage(
          imageDataUrl,
          "PNG",
          xPos,
          yPos,
          renderWidth,
          renderHeight
        );
        return renderHeight;
      } catch (e) {
        console.error("Error loading image:", e);
        return 50;
      }
    };

    if (template?.logo) {
      headerImageHeight = await drawHeaderImage(template?.logo);
    } else {
      headerImageHeight = 50;
    }

    // FROM/TO sections
    currentY = headerImageHeight + 30;
    const lineSpacing = 12;
    const fromToY = currentY;
    const columnWidth = (pageWidth - contentMargin) / 2;

    // FROM section
    const fromX = contentMargin;
    doc.setFont("times", "bold").setFontSize(12);
    doc.text("From:", fromX, fromToY);

    let fromY = fromToY + 12;
    let fromLineCount = 0;
    const maxFromLines = 6;

    doc.setFont("times", "normal").setFontSize(12);
    doc.text(template?.from?.name || "-", fromX, fromY);
    fromY += lineSpacing;
    fromLineCount++;

    if (fromLineCount < maxFromLines) {
      const addressText = template?.from?.address || "-";
      const availableWidth = columnWidth - 20;
      const addressLines = splitTextToWidth(doc, addressText, availableWidth);

      const maxAddressLines = Math.min(2, maxFromLines - fromLineCount - 1);
      let displayLines = addressLines.slice(0, maxAddressLines);

      if (addressLines.length > maxAddressLines) {
        if (displayLines.length > 0) {
          const lastLine = displayLines[displayLines.length - 1];
          const maxLastLineWidth = availableWidth - doc.getTextWidth("...");
          let truncatedLine = lastLine;
          while (
            doc.getTextWidth(truncatedLine) > maxLastLineWidth &&
            truncatedLine.length > 0
          ) {
            truncatedLine = truncatedLine.slice(0, -1);
          }
          displayLines[displayLines.length - 1] = truncatedLine + "...";
        }
      }

      displayLines.forEach((line, index) => {
        if (fromLineCount < maxFromLines) {
          doc.text(line, fromX, fromY);
          fromY += lineSpacing;
          fromLineCount++;
        }
      });
    }

    if (fromLineCount < maxFromLines) {
      const contactText = template?.from?.contact || "-";
      const availableWidth = columnWidth - 20;
      let displayContact = contactText;

      if (doc.getTextWidth(contactText) > availableWidth) {
        const maxContactWidth = availableWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayContact) > maxContactWidth &&
          displayContact.length > 0
        ) {
          displayContact = displayContact.slice(0, -1);
        }
        displayContact += "...";
      }

      doc.text(displayContact, fromX, fromY);
      fromY += lineSpacing;
      fromLineCount++;
    }

    // TO section
    const toX = contentMargin + columnWidth;
    doc.setFont("times", "bold").setFontSize(12);
    doc.text("To:", toX, fromToY);

    let toY = fromToY + 12;
    let toLineCount = 0;
    const maxToLines = 6;

    doc.setFont("times", "normal").setFontSize(12);
    doc.text(log?.to?.name || "-", toX, toY);
    toY += lineSpacing;
    toLineCount++;

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const tinLabel = "TIN no.:";
      const tinLabelWidth = doc.getTextWidth(tinLabel);
      doc.text(tinLabel, toX, toY);
      doc.setFont("times", "normal");
      doc.text(log?.to?.tinNo || "-", toX + tinLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const addressLabel = "Address:";
      const addressLabelWidth = doc.getTextWidth(addressLabel);
      doc.text(addressLabel, toX, toY);

      const addressText = log?.to?.address || "-";
      const availableWidth = columnWidth - 20 - addressLabelWidth - 5;
      const addressLines = splitTextToWidth(doc, addressText, availableWidth);

      let displayLines = addressLines.slice(
        0,
        Math.min(2, maxToLines - toLineCount)
      );
      if (
        addressLines.length > 2 ||
        (toLineCount + displayLines.length >= maxToLines &&
          addressLines.length > displayLines.length)
      ) {
        if (displayLines.length > 0) {
          const lastLine = displayLines[displayLines.length - 1];
          const maxLastLineWidth = availableWidth - doc.getTextWidth("...");
          let truncatedLine = lastLine;
          while (
            doc.getTextWidth(truncatedLine) > maxLastLineWidth &&
            truncatedLine.length > 0
          ) {
            truncatedLine = truncatedLine.slice(0, -1);
          }
          displayLines[displayLines.length - 1] = truncatedLine + "...";
        }
      }

      doc.setFont("times", "normal");
      displayLines.forEach((line, index) => {
        if (toLineCount < maxToLines) {
          if (index === 0) {
            doc.text(line, toX + addressLabelWidth + 5, toY);
          } else {
            doc.text(
              line,
              toX + addressLabelWidth + 5,
              toY + index * lineSpacing
            );
          }
        }
      });

      const addressLineCount = Math.min(
        displayLines.length,
        maxToLines - toLineCount
      );
      toY += addressLineCount * lineSpacing;
      toLineCount += addressLineCount;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const contactLabel = "Contact:";
      const contactLabelWidth = doc.getTextWidth(contactLabel);
      doc.text(contactLabel, toX, toY);

      doc.setFont("times", "normal");
      const contactText = log?.to?.contact || "-";
      const availableContactWidth = columnWidth - 20 - contactLabelWidth - 5;

      let displayContact = contactText;
      if (doc.getTextWidth(contactText) > availableContactWidth) {
        const maxContactWidth = availableContactWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayContact) > maxContactWidth &&
          displayContact.length > 0
        ) {
          displayContact = displayContact.slice(0, -1);
        }
        displayContact += "...";
      }

      doc.text(displayContact, toX + contactLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const repLabel = "Rep.:";
      const repLabelWidth = doc.getTextWidth(repLabel);
      doc.text(repLabel, toX, toY);

      doc.setFont("times", "normal");
      const repText = log?.to?.rep || "-";
      const availableRepWidth = columnWidth - 20 - repLabelWidth - 5;

      let displayRep = repText;
      if (doc.getTextWidth(repText) > availableRepWidth) {
        const maxRepWidth = availableRepWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayRep) > maxRepWidth &&
          displayRep.length > 0
        ) {
          displayRep = displayRep.slice(0, -1);
        }
        displayRep += "...";
      }

      doc.text(displayRep, toX + repLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    currentY = Math.max(fromY, toY) + 30;

    // Check space for date
    if (currentY + 20 > pageHeight - margin - footerHeight) {
      doc.addPage();
      currentY = margin + 30;
    }

    doc.setFontSize(12);
    doc.text(`Date: ${formatDate(log?.nteDate)}`, contentMargin, currentY);
    currentY += 40;

    // Check space for header
    if (currentY + 40 > pageHeight - margin - footerHeight) {
      doc.addPage();
      currentY = margin + 30;
    }

    const headerText = template?.name;
    const fontSize = 14;
    const letterSpacing = 1.5;
    const currentYPosition = currentY;

    doc.setFont("times", "bold");
    doc.setFontSize(fontSize);

    const totalTextWidth =
      headerText
        .split("")
        .reduce((acc, char) => acc + doc.getTextWidth(char), 0) +
      (headerText.length - 1) * letterSpacing;

    const startX = (pageWidth - totalTextWidth) / 2;

    let x = startX;
    for (let i = 0; i < headerText.length; i++) {
      const char = headerText[i];
      doc.text(char, x, currentYPosition);
      const charWidth = doc.getTextWidth(char);
      x += charWidth + letterSpacing;
    }

    const underlineY = currentYPosition + 1;
    doc.line(startX, underlineY, startX + totalTextWidth, underlineY);

    // Subject line with space check
    if (log?.subject && log?.subject.trim() !== "") {
      if (currentY + 40 > pageHeight - margin - footerHeight) {
        doc.addPage();
        currentY = margin + 30;
      }
      currentY += 40;
      doc.setFont("times", "bolditalic");
      doc.text(`InRe: ${log?.subject}`, contentMargin, currentY);
    }

    currentY += 30;

    // Check space for letter opening
    if (currentY + 20 > pageHeight - margin - footerHeight) {
      doc.addPage();
      currentY = margin + 30;
    }

    const maxTextWidth = pageWidth - 2 * contentMargin;
    const lineHeight = 14;

    doc.setFont("times", "normal");
    doc.text(`${log?.letterOpening}`, contentMargin, currentY);
    currentY += 30;

    // Helper function to add justify alignment to HTML if not present
    const ensureJustified = (htmlText) => {
      if (!htmlText) return htmlText;

      // Parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlText;

      // Add justify class to all paragraphs that don't have alignment
      const paragraphs = tempDiv.querySelectorAll("p");
      paragraphs.forEach((p) => {
        if (
          !p.className.includes("ql-align-") &&
          (!p.style.textAlign || p.style.textAlign === "left")
        ) {
          p.style.textAlign = "justify";
        }
      });

      return tempDiv.innerHTML;
    };

    // Render NTE body with footer consideration and justified text
    if (log?.body1 && log?.body1.trim() !== "") {
      currentY = drawJustifiedParagraphs(
        doc,
        ensureJustified(log?.body1), // ← Apply justify
        contentMargin,
        currentY,
        maxTextWidth,
        lineHeight,
        pageHeight,
        40,
        0,
        footerHeight,
        0,
        40,
        5,
        40
      );
    }

    currentY += 30;

    // Closing section with footer space check
    const closingSectionHeight = 30 + 15 + 15;
    if (currentY + closingSectionHeight > pageHeight - 40 - footerHeight) {
      doc.addPage();
      currentY = margin + 30;
    }

    doc.text(
      log?.closingRemarks?.complimentary
        ? log?.closingRemarks?.complimentary
        : "Respectfully,",
      contentMargin,
      currentY
    );
    currentY += 30;

    if (log?.closingRemarks.name && log?.closingRemarks.name.trim() !== "") {
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      const nameUpper = log?.closingRemarks.name.toUpperCase();
      const nameWidth = doc.getTextWidth(nameUpper);

      doc.text(nameUpper, contentMargin, currentY);
      currentY += 5;

      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(
        contentMargin,
        currentY,
        contentMargin + nameWidth + 10,
        currentY
      );
      currentY += 15;
    }

    doc.setFont("times", "normal");
    doc.text(log?.closingRemarks.position, contentMargin, currentY);
    currentY += 15;
    doc.text(log?.closingRemarks.company, contentMargin, currentY);

    // FIXED: Add footers to main document pages with proper await and page count
    const mainDocumentPageCount = doc.getNumberOfPages();
    if (template?.footer) {
      await addFooterToPages(
        doc,
        template?.footer,
        pageWidth,
        pageHeight,
        mainDocumentPageCount
      );
    }
  } else {
    // Non-NTE document rendering
    const headerWidth = pageWidth - 2 * margin;
    const logoWidth = headerWidth / 3;
    const companyInfoX = margin + logoWidth + 20;

    if (template?.logo) {
      try {
        const logoDataUrl = await getImageDataUrl(template?.logo);
        if (logoDataUrl) {
          const imgProps = doc.getImageProperties(logoDataUrl);
          const aspectRatio = imgProps.width / imgProps.height;

          const maxLogoHeight = 140;
          let logoHeight = maxLogoHeight;
          let logoRenderWidth = logoHeight * aspectRatio;

          if (logoRenderWidth > logoWidth - 20) {
            logoRenderWidth = logoWidth - 20;
            logoHeight = logoRenderWidth / aspectRatio;
          }

          doc.addImage(
            logoDataUrl,
            "PNG",
            margin,
            currentY,
            logoRenderWidth,
            logoHeight
          );
        }
      } catch (error) {
        console.error("Error adding logo:", error);
      }
    }

    const companyInfoWidth = pageWidth - companyInfoX - margin;

    doc.setFont("times", "bold");
    doc.setFontSize(11);
    doc.text(template?.from.name, pageWidth - margin, currentY + 10, {
      align: "right",
    });

    doc.setFont("times", "normal");
    doc.setFontSize(9);

    const addressLines = doc.splitTextToSize(
      template?.from.address,
      companyInfoWidth
    );

    let addressY = currentY + 25;
    addressLines.forEach((line) => {
      doc.text(line, pageWidth - margin, addressY, { align: "right" });
      addressY += 12;
    });

    doc.text(template?.from.contact, pageWidth - margin, addressY, {
      align: "right",
    });
    addressY += 12;

    if (template?.from.website) {
      doc.text(template?.from.website, pageWidth - margin, addressY, {
        align: "right",
      });
    }

    const metLeftColX = 200;
    currentY += 50;

    // Check space before drawing line
    if (currentY + 60 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.setDrawColor(105, 105, 105);
    doc.setLineWidth(1);
    doc.line(metLeftColX, currentY, pageWidth - margin, currentY);
    currentY += 30;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont("times", "normal");
    doc.text(
      `Date: ${formatDate(log?.createdAt) || "-"}`,
      metLeftColX,
      currentY
    );

    currentY += 12;

    // Check space for each metadata line
    if (currentY + 12 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.text(`Priority Type: ${log?.priority || "-"}`, metLeftColX, currentY);
    currentY += 12;

    if (currentY + 12 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    if (logType === "server") {
      doc.text(
        `Processed By: ${log?.processedBy || "-"}`,
        metLeftColX,
        currentY
      );
      currentY += 12;
    } else if (logType === "secretary") {
      doc.text(
        `Assisted/Delegated To: ${log?.assistedTo || "-"}`,
        metLeftColX,
        currentY
      );
      currentY += 12;
    }

    if (currentY + 12 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.text("Remarks: ", metLeftColX, currentY);
    const remarksX = doc.getTextWidth("Remarks: ");

    doc.setFont("times", "italic");
    doc.setFontSize(11);
    const remarksText = parseHTMLToText(log?.remarks);
    const remarksLines = doc.splitTextToSize(
      remarksText,
      pageWidth - metLeftColX - margin - remarksX
    );

    remarksLines.forEach((line) => {
      if (currentY + 12 > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      doc.text(line, remarksX + metLeftColX, currentY);
      currentY += 12;
    });

    if (currentY + 12 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    const getStatusBgColor = (status) => {
      const colors = {
        "In Progress": "rgb(50, 50, 168)",
        "Not Started": "rgb(0,0,0)",
        Interrupted: "rgb(168, 50, 50)",
        Completed: "rgb(50, 168, 50)",
      };
      return colors[status] || "#9ca3af";
    };

    doc.setFont("times", "normal");
    doc.text(`Status: `, metLeftColX, currentY);
    doc.setTextColor(getStatusBgColor(log?.status));
    doc.setFont("times", "bold");
    const statusX = doc.getTextWidth("Status: ");
    doc.text(`${log?.status}`, statusX + metLeftColX, currentY);

    currentY += 30;

    // Check space for TO section
    if (currentY + 60 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text("To:", metLeftColX, currentY);

    if (log?.to) {
      doc.setFont("times", "bold");
      doc.text(log?.to.name || "-", metLeftColX + 20, currentY);
      currentY += 12;

      if (log?.to.rep) {
        if (currentY + 12 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setFont("times", "normal");
        doc.text("Rep:", metLeftColX + 20, currentY);
        doc.text(log?.to.rep, metLeftColX + 60, currentY);
        currentY += 12;
      }

      if (log?.to.contact) {
        if (currentY + 12 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setFont("times", "normal");
        doc.text("Contact:", metLeftColX + 20, currentY);
        doc.text(log?.to.contact, metLeftColX + 60, currentY);
        currentY += 12;
      }

      if (logType === "server" && log?.to.tinNo) {
        if (currentY + 12 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setFont("times", "normal");
        doc.text("TIN No:", metLeftColX + 20, currentY);
        doc.text(log?.to.tinNo, metLeftColX + 60, currentY);
        currentY += 12;
      }

      if (logType === "server" && log?.to.email) {
        if (currentY + 12 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setFont("times", "normal");
        doc.text("Email:", metLeftColX + 20, currentY);
        doc.text(log?.to.email, metLeftColX + 60, currentY);
        currentY += 12;
      }

      if (logType === "server" && log?.to.address) {
        if (currentY + 12 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }
        doc.setFont("times", "normal");
        doc.text("Address:", metLeftColX + 20, currentY);
        const addressText = doc.splitTextToSize(
          log?.to.address,
          pageWidth - metLeftColX - 80 - margin
        );
        addressText.forEach((line, idx) => {
          if (currentY + 12 > pageHeight - margin) {
            doc.addPage();
            currentY = margin;
          }
          doc.text(line, metLeftColX + (idx === 0 ? 60 : 20), currentY);
          if (idx < addressText.length - 1) currentY += 12;
        });
        currentY += 12;
      }
    }

    currentY += 30;

    // Check space before line separator
    if (currentY + 30 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.setDrawColor(105, 105, 105);
    doc.setLineWidth(1);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 30;

    // Check space for title section
    if (currentY + 40 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    const title = template?.name;
    const headerText = `${title}`.toUpperCase();
    const fontSize = 14;
    const letterSpacing = 1.5;
    const currentYPosition = currentY;

    doc.setFont("times", "bold");
    doc.setFontSize(fontSize);

    const totalTextWidth =
      headerText
        .split("")
        .reduce((acc, char) => acc + doc.getTextWidth(char), 0) +
      (headerText.length - 1) * letterSpacing;

    const startX = (pageWidth - totalTextWidth) / 2;

    let x = startX;
    for (let i = 0; i < headerText.length; i++) {
      const char = headerText[i];
      doc.text(char, x, currentYPosition);
      const charWidth = doc.getTextWidth(char);
      x += charWidth + letterSpacing;
    }

    const underlineY = currentYPosition + 1;
    doc.line(startX, underlineY, startX + totalTextWidth, underlineY);
    currentY += 30;

    // Check space before table
    if (currentY + 60 > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    const tableData = [["Task", log?.task || "-"]];
    // console.log(logType);
    if (logType === "server") {
      tableData.push(["Agency", log?.agency]);
    }
    tableData.push(["Date Completed", formatDate(log?.dateCompleted)]);

    doc.autoTable({
      startY: currentY,
      head: [],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
      },
      columnStyles: {
        0: {
          fontStyle: "bold",
          cellWidth: 150,
          halign: "left",
          valign: "top",
        },
        1: {
          cellWidth: pageWidth - 2 * margin - 150,
          halign: "left",
          valign: "top",
        },
      },
      margin: { left: margin, right: margin },
    });

    currentY = doc.lastAutoTable.finalY + 10;

    if (logType === "server") {
      const renderFormattedSection = (title, htmlContent) => {
        if (!htmlContent) return;

        currentY += 10;

        if (currentY + 60 > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        doc.setFont("times", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(title, margin + 5, currentY + 5);

        currentY += 20;

        const items = parseHTMLToFormattedText(htmlContent);
        const contentX = margin + 10;
        const maxWidth = pageWidth - contentX - margin - 10;

        doc.setFont("times", "normal");
        doc.setFontSize(10);

        items.forEach((item) => {
          if (currentY + 15 > pageHeight - margin) {
            doc.addPage();
            currentY = margin;
          }

          if (item.type === "bullet") {
            doc.circle(contentX, currentY - 2, 2, "F");

            const textLines = doc.splitTextToSize(item.text, maxWidth - 15);
            textLines.forEach((line, idx) => {
              if (idx > 0 && currentY + 12 > pageHeight - margin) {
                doc.addPage();
                currentY = margin;
              }
              doc.text(line, contentX + 10, currentY);
              if (idx < textLines.length - 1) currentY += 12;
            });

            currentY += 15;
          } else {
            const textLines = doc.splitTextToSize(item.text, maxWidth);
            textLines.forEach((line, idx) => {
              if (idx > 0 && currentY + 12 > pageHeight - margin) {
                doc.addPage();
                currentY = margin;
              }
              doc.text(line, contentX, currentY);
              if (idx < textLines.length - 1) currentY += 12;
            });

            currentY += 15;
          }
        });

        currentY += 5;
      };

      if (log?.toProcess) {
        renderFormattedSection("To Process", log?.toProcess);
      }

      if (log?.dependencies) {
        renderFormattedSection("Dependencies", log?.dependencies);
      }

      if (log?.processUpdate) {
        renderFormattedSection("Process Update", log?.processUpdate);
      }
    }

    currentY += 20;

    // Check space for prepared by section
    const preparedByArray = log?.preparedBy || [];
    const hasPreparedBy =
      Array.isArray(preparedByArray) && preparedByArray.length > 0;

    const estimatedHeight = hasPreparedBy
      ? preparedByArray.length * 80 + 30
      : 80;

    if (currentY + estimatedHeight > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    doc.setFont("times", "bold");
    doc.setFontSize(10);
    doc.text("Prepared by:", margin, currentY);
    currentY += 30;

    if (hasPreparedBy) {
      preparedByArray.forEach((person) => {
        if (currentY + 70 > pageHeight - margin) {
          doc.addPage();
          currentY = margin + 30;
        }

        const signatureLineWidth = 200;
        const lineStartX = margin;
        const lineCenterX = lineStartX + signatureLineWidth / 2;

        doc.setFont("times", "bold");
        doc.setFontSize(10);
        const personName = (person.name || "").toUpperCase();
        doc.text(personName, lineCenterX, currentY, { align: "center" });

        doc.setLineWidth(0.5);
        doc.line(
          lineStartX,
          currentY + 2,
          lineStartX + signatureLineWidth,
          currentY + 2
        );
        currentY += 15;

        doc.setFont("times", "normal");
        doc.setFontSize(9);
        if (person.position) {
          doc.text(person.position, lineCenterX, currentY, { align: "center" });
          currentY += 12;
        }

        if (person.company) {
          doc.text(person.company, lineCenterX, currentY, { align: "center" });
        }

        currentY += 40;
      });
    }
  }

  // Add page numbers and control numbers to all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);

    doc.setFont("times", "italic");
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - contentMargin,
      pageHeight - 15,
      {
        align: "right",
      }
    );
    doc.setFont("times", "normal");
    doc.text(
      `Control No.: ${
        log?.firebaseId ? log?.firebaseId : "Generated upon save"
      }`,
      margin,
      pageHeight - 15,
      {
        align: "left",
      }
    );
  }

  if (action === "download") {
    const filename = `${template?.name}_${log?.firebaseId}.pdf`;
    doc.save(filename);
  }

  return doc.output("bloburl");
};

export default generateLogsPDF;
